'use client'

import * as React from 'react'

import * as ContextMenuPrimitive from '@radix-ui/react-context-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'

import { Tooltip } from '~/components/component-library'
import { cn } from '~/utils/tailwind-utils'

const ContextMenu = ({
  modal = false,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Root>) => (
  <ContextMenuPrimitive.Root modal={modal} {...props} />
)

ContextMenu.displayName = ContextMenuPrimitive.Root.displayName

const ContextMenuTrigger = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Trigger> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof ContextMenuPrimitive.Trigger
  > | null>
}) => (
  <ContextMenuPrimitive.Trigger
    ref={ref}
    className={cn(className)}
    {...props}
  />
)

ContextMenuTrigger.displayName = ContextMenuPrimitive.Trigger.displayName

const ContextMenuSubTrigger = ({
  ref,
  className,
  inset,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubTrigger> & {
  inset?: boolean
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof ContextMenuPrimitive.SubTrigger
  > | null>
}) => (
  <ContextMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'flex cursor-pointer select-none items-center rounded px-3 py-2 text-sm outline-none hover:bg-surface-muted',
      inset && 'pl-8',
      className,
    )}
    {...props}
  >
    {children}
    <ChevronRight className="ml-auto h-4 w-4" />
  </ContextMenuPrimitive.SubTrigger>
)
ContextMenuSubTrigger.displayName = ContextMenuPrimitive.SubTrigger.displayName

const ContextMenuSubContent = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubContent> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof ContextMenuPrimitive.SubContent
  > | null>
}) => (
  <ContextMenuPrimitive.SubContent
    ref={ref}
    className={cn('anchored z-50 min-w-[8rem] overflow-hidden p-1', className)}
    {...props}
  />
)
ContextMenuSubContent.displayName = ContextMenuPrimitive.SubContent.displayName

const ContextMenuContent = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof ContextMenuPrimitive.Content
  > | null>
}) => (
  <ContextMenuPrimitive.Portal>
    <ContextMenuPrimitive.Content
      ref={ref}
      className={cn(
        'anchored z-50 w-80 min-w-[8rem] overflow-hidden p-1',
        className,
      )}
      {...props}
    />
  </ContextMenuPrimitive.Portal>
)
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName

const ContextMenuItem = ({
  ref,
  className,
  inset,
  disabled,
  tooltip,
  onClick,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Item> & {
  inset?: boolean
  className?: string
  disabled?: boolean
  tooltip?: string
  onClick?: (event: React.MouseEvent) => void
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof ContextMenuPrimitive.Item
  > | null>
}) => {
  const item = (
    <ContextMenuPrimitive.Item
      ref={ref}
      onClick={(event) => {
        event.stopPropagation()
        onClick?.(event)
      }}
      className={cn(
        'relative flex cursor-pointer select-none items-center rounded px-3 py-2 text-sm outline-none transition-colors hover:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:hover:bg-gray-700',
        {
          'pl-8': inset,
          'pointer-events-none opacity-50': disabled,
        },
        className,
      )}
      {...props}
    />
  )
  if (tooltip) {
    return (
      <Tooltip triggerClassName="w-full" content={tooltip}>
        {item}
      </Tooltip>
    )
  }
  return item
}
ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName

const ContextMenuCheckboxItem = ({
  ref,
  className,
  children,
  checked,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.CheckboxItem> & {
  className?: string
  checked?: boolean
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof ContextMenuPrimitive.CheckboxItem
  > | null>
}) => (
  <ContextMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-surface-muted focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:text-gray-50',
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <ContextMenuPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </ContextMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </ContextMenuPrimitive.CheckboxItem>
)
ContextMenuCheckboxItem.displayName =
  ContextMenuPrimitive.CheckboxItem.displayName

const ContextMenuRadioItem = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.RadioItem> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof ContextMenuPrimitive.RadioItem
  > | null>
}) => (
  <ContextMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-surface-muted focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:text-gray-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <ContextMenuPrimitive.ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </ContextMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </ContextMenuPrimitive.RadioItem>
)
ContextMenuRadioItem.displayName = ContextMenuPrimitive.RadioItem.displayName

const ContextMenuLabel = ({
  ref,
  className,
  inset,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Label> & {
  inset?: boolean
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof ContextMenuPrimitive.Label
  > | null>
}) => (
  <ContextMenuPrimitive.Label
    ref={ref}
    className={cn(
      'px-3 py-2 text-sm font-semibold',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
)
ContextMenuLabel.displayName = ContextMenuPrimitive.Label.displayName

const ContextMenuSeparator = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof ContextMenuPrimitive.Separator
  > | null>
}) => (
  <ContextMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-gray-200 dark:bg-gray-700', className)}
    {...props}
  />
)
ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName

const ContextMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & {
  className?: string
}) => {
  return (
    <span
      className={cn('ml-auto text-xs tracking-widest opacity-60', className)}
      {...props}
    />
  )
}
ContextMenuShortcut.displayName = 'ContextMenuShortcut'

export {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
}
